import React from 'react';
import './EventDetails.less';
import { inject, observer } from 'mobx-react';
import { Row, Col, Card, Carousel, AppLocaleProvider } from 'sd-ui-components';
import AppLayout from '../AppLayout/AppLayout';
import BackBreadcrumb from '../Common/BackBreadcrumb';
import PageLoadLogger from '../../utils/PageLoadLogger';
import CarouselDots from './CarouselDots';
import EventSummary from './EventSummary';
import EventTabs from './EventTabs';
import Notifications from '../../components/Notifications/Notifications';
import { computed } from 'mobx';

const { FormattedMessage } = AppLocaleProvider;

@inject('ConfigStore')
@inject('ObservationsStore')
@inject('EventsStore')
@observer
class ObservationEvents extends React.Component {
  state = {
    // There is a bug in react-slick which makes fromIndex undefined on beforeChange, so we have to store it ourselves.
    activeCarouselIndex: undefined
  };

  activeVideoControls = {
    pause: undefined,
    play: undefined
  };

  pageLoadLogger = new PageLoadLogger('Event details');

  async componentDidMount() {
    const observationName = this.props.observationName;
    const eventNumber = this.props.eventNumber;

    this.props.ObservationsStore.selectedObservation = observationName;
    this.props.EventsStore.setSelectedEventNumber(eventNumber);
    await this.props.EventsStore.loadAllEventDetailsForObservation(
      observationName
    );

    if (this.carouselRef) {
      const events = this.detailedEventList;
      const index = events.findIndex(
        event => event.eventNumber === eventNumber
      );

      if (index >= 0) {
        this.carouselRef.goTo(index);
      } else {
        this.carouselRef.goTo(0);
      }

      this.setState({ activeCarouselIndex: index });
    }

    this.pageLoadLogger.capture();
  }

  componentWillUnmount() {
    this.props.EventsStore.cancelLoadEventDetails();
  }

  onCarouselGoToIndex = toIndex => {
    if (this.carouselRef) {
      this.carouselRef.goTo(toIndex);
      this.setState({ activeCarouselIndex: toIndex });
    }
  };

  carouselBeforeChange = (fromIndex, toIndex) => {
    const { EventsStore } = this.props;

    const event = this.detailedEventList[toIndex];
    EventsStore.pauseActiveVideo();

    if (event) {
      this.props.EventsStore.setSelectedEventNumber(event.eventNumber);
    }
  };

  carouselAfterChange = activeEventIndex => {
    const { EventsStore } = this.props;

    this.setState({ activeCarouselIndex: activeEventIndex }, () => {
      const eventWithDetails = this.detailedEventList[activeEventIndex];

      if (
        EventsStore.activeTab === EventsStore.tabKeys.video &&
        !EventsStore.eventHasVideo(eventWithDetails.eventNumber)
      ) {
        EventsStore.setActiveTab(EventsStore.tabKeys.details);
      } else if (EventsStore.activeTab === EventsStore.tabKeys.video) {
        EventsStore.pauseActiveVideo();
      }
    });
  };

  @computed
  get detailedEventList() {
    const detailedEventList = this.props.EventsStore
      .eventDetailsForSelectedObservation;
    return this.props.EventsStore.sortEventsByRecordedDate(detailedEventList);
  }

  @computed
  get selectedEventIndex() {
    const detailedEventList = this.detailedEventList;
    return detailedEventList.findIndex(
      event => event.eventNumber === this.props.EventsStore.selectedEventNumber
    );
  }

  render() {
    const { EventsStore } = this.props;

    return (
      <AppLayout>
        <BackBreadcrumb
          to={`/observation/${encodeURIComponent(
            this.props.ObservationsStore.selectedObservationName
          )}`}
          data-automation-id="sd-ui-driver-navigation-breadcrumb-observation"
        >
          <FormattedMessage
            id="App.Navigation.BackToObservation"
            defaultMessage="Back to observation"
          />
        </BackBreadcrumb>

        <Row>
          <Col span={24}>
            <Notifications />
          </Col>
        </Row>

        {!EventsStore.loadingErrors.eventDetails && (
          <Row gutter={16}>
            <Col span={24}>
              <Carousel
                externalNavigation={true}
                className="event-details-carousel"
                dots={true}
                beforeChange={this.carouselBeforeChange}
                afterChange={this.carouselAfterChange}
                data-automation-id="sd-ui-driver-eventdetails-carousel"
                infinite={false}
                ref={c => (this.carouselRef = c)}
                appendDots={dots => (
                  <CarouselDots
                    dots={dots}
                    selectedIndex={this.selectedEventIndex}
                    onGoToIndex={this.onCarouselGoToIndex}
                  />
                )}
              >
                {this.detailedEventList.map((event, eventIndex) => (
                  <div key={event.eventNumber || eventIndex}>
                    {!event.eventNumber && (
                      <Card key={eventIndex} loading={true} />
                    )}

                    {event.eventNumber && (
                      <Card
                        key={event.eventNumber}
                        className="event-detail-card"
                        loading={eventIndex !== this.state.activeCarouselIndex}
                      >
                        {eventIndex === this.state.activeCarouselIndex && (
                          <div>
                            <Row className="event-index-row">
                              <Col span={24}>
                                <FormattedMessage
                                  id="App.EventDetails.EventIndex"
                                  defaultMessage="(Event {eventNumber} of {eventsCount})"
                                  values={{
                                    eventNumber: eventIndex + 1,
                                    eventsCount: this.detailedEventList.length
                                  }}
                                />
                              </Col>
                            </Row>

                            <EventSummary event={event} />
                            <EventTabs event={event} />
                          </div>
                        )}
                      </Card>
                    )}
                  </div>
                ))}
              </Carousel>
            </Col>
          </Row>
        )}
      </AppLayout>
    );
  }
}

export default ObservationEvents;



// WEBPACK FOOTER //
// ./src/containers/EventDetails/ObservationEvents.jsx